import { Autocomplete, TextField } from "@mui/material";

import { fuzzyFilter } from "@/shared/text/fuzzyFilter";

import styles from "./FuzzySelect.module.css";

export interface FuzzySelectProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  className?: string;
}

/**
 * Single-select combobox: a free-text input that fuzzy-filters `options`
 * (subsequence match, ranked) as the user types. The committed value is always
 * one of `options` — typing only narrows the suggestions. When the field still
 * shows the committed value untouched, the full list is offered so the user can
 * browse rather than only seeing matches of the current value.
 */
export function FuzzySelect({
  label,
  value,
  options,
  onChange,
  className,
}: FuzzySelectProps) {
  return (
    <Autocomplete
      className={className}
      size="small"
      autoHighlight
      disableClearable
      options={options}
      value={value}
      onChange={(_event, next) => onChange(next)}
      filterOptions={(opts, state) =>
        state.inputValue === value ? opts : fuzzyFilter(state.inputValue, opts)
      }
      slotProps={{
        popper: {
          className: styles.popper,
          // Keep the (content-wide) dropdown anchored to the input's left edge:
          // bottom-start, and disable start↔end variation flipping so it grows
          // rightward instead of right-aligning when it would overflow.
          placement: "bottom-start",
          modifiers: [{ name: "flip", options: { flipVariations: false } }],
        },
      }}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
}
